"use client";

import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Facebook, Instagram, Linkedin, Send } from "lucide-react";
import { toast } from "sonner";

const footerNav = {
  products: [
    { name: "Semiconductors", href: "/products?category=semiconductors" },
    { name: "Passive Components", href: "/products?category=passives" },
    { name: "Connectors", href: "/products?category=connectors" },
    { name: "Sensors", href: "/products?category=sensors" },
    { name: "All Products", href: "/products" },
  ],
  company: [
    { name: "About Us", href: "/about" },
    { name: "Manufacturers", href: "/manufacturers" },
    { name: "Services", href: "/services" },
    { name: "Industries", href: "/industries" },
    { name: "Careers", href: "/careers" },
    { name: "Contact Us", href: "/contact" },
  ],
  support: [
    { name: "Help Center", href: "/support" },
    { name: "Technical Support", href: "/support/technical" },
    { name: "Return Policy", href: "/support/returns" },
    { name: "Shipping Information", href: "/support/shipping" },
  ],
  legal: [
    { name: "Privacy Policy", href: "/legal/privacy" },
    { name: "Terms of Service", href: "/legal/terms" },
    { name: "Cookie Policy", href: "/legal/cookies" },
  ],
};

const socialLinks = [
  {
    name: "Facebook",
    icon: Facebook,
    href: "https://www.facebook.com/people/ProSoft-Electronics/61574513482694/",
  },
  {
    name: "Instagram",
    icon: Instagram,
    href: "https://www.instagram.com/prosoft.electronics?igsh=YjRpajBwZndqeWs4",
  },
  {
    name: "LinkedIn",
    icon: Linkedin,
    href: "https://www.linkedin.com/company/prosoft-electronics",
  },
];

export function Footer(): JSX.Element {
  return (
    <footer className="bg-slate-900 text-slate-300" role="contentinfo">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        {/* Top Section: Logo, Nav Links, Newsletter */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12">
          {/* Logo and Description - Full width on mobile, spans 4 columns on large screens */}
          <div className="sm:col-span-2 lg:col-span-4 xl:col-span-3">
            <Link href="/" className="inline-block mb-4 sm:mb-6">
              <Image
                src="/prosoft-logo.webp"
                alt="ProSoft Electronics"
                width={160}
                height={35}
                className="h-8 sm:h-9 w-auto"
              />
            </Link>
            <p className="text-sm sm:text-base leading-relaxed text-slate-400 max-w-sm">
              Where experience meets purpose, delivering quality electronic
              components, testing, and manufacturing services that help
              businesses build with confidence.{" "}
            </p>
          </div>

          {/* Navigation Links - Responsive grid */}
          <div className="sm:col-span-2 lg:col-span-6 xl:col-span-6 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-white mb-3 sm:mb-4">
                Products
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                {footerNav.products.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className="text-xs sm:text-sm text-slate-400 hover:text-white transition-colors duration-200"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-white mb-3 sm:mb-4">
                Company
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                {footerNav.company.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className="text-xs sm:text-sm text-slate-400 hover:text-white transition-colors duration-200"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-white mb-3 sm:mb-4">
                Support
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                {footerNav.support.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className="text-xs sm:text-sm text-slate-400 hover:text-white transition-colors duration-200"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-white mb-3 sm:mb-4">
                Legal
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                {footerNav.legal.map((item) => (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className="text-xs sm:text-sm text-slate-400 hover:text-white transition-colors duration-200"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Newsletter Signup - Full width on mobile, spans 2 columns on large screens */}
          <div className="sm:col-span-2 lg:col-span-2 xl:col-span-3 mt-6 sm:mt-0">
            <h3 className="text-sm sm:text-base font-semibold text-white mb-3 sm:mb-4">
              Subscribe to our newsletter
            </h3>
            <p className="text-xs sm:text-sm text-slate-400 mb-4 leading-relaxed">
              Get the latest industry news, product updates, and special offers.
            </p>
            <form
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget as HTMLFormElement;
                const fd = new FormData(form);
                const email = (fd.get("newsletterEmail") as string) || "";
                const emailTrim = email.trim();
                const emailRegex = /^\S+@\S+\.\S+$/;
                if (!emailTrim || !emailRegex.test(emailTrim)) {
                  toast.error("Please enter a valid email address");
                  return;
                }
                // Mock subscribe behavior
                toast.success("Subscribed to newsletter!");
                form.reset();
              }}
            >
              <Input
                name="newsletterEmail"
                type="email"
                placeholder="Enter your email"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-white flex-grow text-sm sm:text-base h-10 sm:h-11"
                aria-label="Email for newsletter"
              />
              <Button
                type="submit"
                size="icon"
                aria-label="Subscribe"
                className="flex-shrink-0 h-10 sm:h-11 w-10 sm:w-11"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>

        <Separator className="my-8 sm:my-10 lg:my-12 bg-slate-800" />

        {/* Bottom Section: Copyright and Social Links */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
          <div className="text-xs sm:text-sm text-slate-500 text-center sm:text-left order-2 sm:order-1">
            <p>&copy; {new Date().getFullYear()} ProSoft Electronics. All rights reserved.</p>
               <p className="text-xs mt-1">
             Created by{" "}
                <a
                href="https://www.bitsolventures.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white underline"
                 >
                  BS Ventures (Pvt) Ltd.
              </a>
              </p>
            <p className="text-xs mt-1">
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-white underline">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="hover:text-white underline">
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 order-1 sm:order-2">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-white transition-colors duration-200 p-1 rounded-md hover:bg-slate-800"
                aria-label={`Follow us on ${social.name}`}
              >
                <social.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="sr-only">{social.name}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
